<template>
  <div class="card">
    <div class="bg-blue-light p-2">
      <TitleButton :showBtn="false" title="Payment Requisition - On Approval" />

      <DateQuerySetter
        :inEnoughSpace="false"
        btnTitle="Go"
        @onClickGo="onClickGo"
      >
        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Voucher Type: </label>
          <v-select
            placeholder="Select Voucher Type"
            v-model="voucherType"
            :options="voucherTypeOptions"
            label="name"
            :reduce="option => option.id"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Txn Type: </label>
          <v-select
            placeholder="Select Txn Type"
            v-model="txnType"
            :options="txnTypeOptions"
            label="name"
            :reduce="option => option.id"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label class="col-form-label">Party: </label>
            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
              <i class="fas fa-search"></i>
            </span>
          </div>
          <AsyncSelect
            placeholder="Select Party"
            v-model="contactProfile"
            :api-service="fetchContactProfiles"
            :format-label="formatPatientLabel"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Status</label>
          <v-select
            placeholder="Select Status"
            v-model="status"
            :options="allStatus"
            label="name"
            :reduce="name => name.id"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="card mt-1 p-2">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Voucher Create Date</th>
              <th>Voucher No</th>
              <th>Party Details</th>
              <th>Item / Account Head</th>
              <th class="text-end">Amount</th>
              <th>Attachment</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="! rows.length">
              <td colspan="8" class="text-center">No payment requisition on approval found.</td>
            </tr>
            <tr v-for="(item, i) in rows" :key="i">
              <td>{{ item.date }}</td>
              <td>{{ item.voucher_no }}</td>
              <td>{{ item.party_name }}</td>
              <td>{{ item.account_head_display }}</td>
              <td class="text-end">{{ commaFormat(item.amount) }}</td>
              <td>
                <button
                  v-if="item.attachments && item.attachments.length"
                  class="btn btn-outline-secondary btn-sm"
                  title="View attachment"
                  @click="viewAttachments(item.attachments)"
                >
                  <i class="fas fa-paperclip"></i>
                </button>
              </td>
              <td>
                <span>{{ item.status }}</span>
              </td>
              <td>
                <button
                  v-if="item.status !== 'approved'"
                  class="btn btn-primary btn-sm"
                  @click="onClickReview(item)"
                >Review</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Pagination
        :offset="offset"
        @onPageChange="onPageChange"
        ref="paginationRef"
      />

      <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        :isFilterable="true"
        :companyRoles="companyRoles"
      />
    </div>

    <ReviewLedgerApprovalModal
      v-if="reviewModalOpen && activeLedgerId"
      :ledger-master-id="activeLedgerId"
      :workflow-master-id="workflowDetails && workflowDetails.id ? workflowDetails.id : null"
      @close="onCloseReviewModal"
      @updated="fetchOnApproval"
    />

    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'

import ReviewLedgerApprovalModal from './ReviewLedgerApprovalModal.vue'

import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import handleContact from '@/services/modules/contact'
import handleRole from '@/services/modules/role'
import handleWorkflow from '@/services/modules/approvalMatrix/workflowMatrix'
import handleInventory from '@/services/modules/inventory'
import figureFormatter from '@/services/utils/figureFormatter'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'

const showError = inject('showError')
const store = useStore()
const route = useRoute()
const router = useRouter()

const { fetchPaymentRequisitionList } = handlePaymentRequisition()
const { fetchContactProfiles } = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { fetchCompanyDefaultRoles } = handleRole()
const { fetchWorkflowFromPageId } = handleWorkflow()
const { fetchHome } = handleInventory()
const { commaFormat } = figureFormatter()

const loading = ref(false)
const rows = ref([])
const contactProfile = ref(null)
const companyRoles = ref([])
const offset = ref(20)
const paginationRef = ref(null)
const workflowDetails = ref(null)

const reviewModalOpen = ref(false)
const activeLedgerId = ref(null)
const status = ref(null)
const homeContent = ref([])
const voucherType = ref(null)
const txnType = ref(null)

const allowedVoucherTypes = ['payment_voucher', 'contra_voucher']
const allowedTxnTypes = ['general_payment', 'bill_payment', 'general_contra']

const voucherTypeOptions = computed(() => {
  let result = []
  if (! homeContent.value.hasOwnProperty('voucher_type')) return result
  allowedVoucherTypes.forEach((key) => {
    if (! homeContent.value.voucher_type.hasOwnProperty(key)) return
    result.push({ id: key, name: homeContent.value.voucher_type[key] })
  })
  return result
})

const txnTypeOptions = computed(() => {
  let result = []
  if (! homeContent.value.hasOwnProperty('txn_type')) return result
  allowedTxnTypes.forEach((key) => {
    if (! homeContent.value.txn_type.hasOwnProperty(key)) return
    result.push({ id: key, name: homeContent.value.txn_type[key] })
  })
  return result
})

const allowedStatuses = ['pending_approve', 'pending_review', 'on_approval']

const allStatus = computed(() => {
  let approvalStatus = []
  if (! homeContent.value.hasOwnProperty('approval_status')) return approvalStatus
  allowedStatuses.forEach((id) => {
    if (! homeContent.value.approval_status.hasOwnProperty(id)) return
    approvalStatus.push({
      id: id,
      name: homeContent.value.approval_status[id],
    })
  })
  return approvalStatus
})

const companyId = computed(() => route.params.companyId)
const pageId = computed(() => route.params.pageId)
const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const page = computed(() => route.query.page)

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  if (contactProfile.value) query += '&contact_profile_id=' + contactProfile.value.id
  if (voucherType.value) query += '&voucher_type=' + voucherType.value
  if (txnType.value) query += '&txn_type=' + txnType.value
  if (status.value) query += '&status=' + status.value
  if (! status.value) query += '&status=' + allowedStatuses.join(',')
  query += '&review=1'
  query += '&page_id=' + pageId.value
  query += '&offset=' + offset.value
  return query
}

const setPagination = (data) => {
  if (! data) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const fetchOnApproval = async () => {
  loading.value = true
  rows.value = []
  try {
    const res = await fetchPaymentRequisitionList(getQuery())
    if (! res.status) {
      store.commit('resetPagination')
      return
    }
    rows.value = res.data.data
    setPagination(res.data)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const fetchWorkflow = async () => {
  const query = `?company_id=${companyId.value}&approval_page_id=${pageId.value}`
  const res = await fetchWorkflowFromPageId(query).catch(() => null)
  if (! res.data) return
  workflowDetails.value = res.data
}

const onClickGo = async () => {
  store.commit('setPaginationData', {})
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = 1
  await router.push({ path: route.path, query: routeQuery })
  await fetchOnApproval()
}

const onPageChange = async (p) => {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = p
  await router.push({ path: route.path, query: routeQuery })
  await fetchOnApproval()
}

const onClickReview = (item) => {
  if (! workflowDetails.value || ! workflowDetails.value.id) {
    showError('No active workflow is configured for this page.')
    return
  }
  activeLedgerId.value = item.ledger_master_id
  reviewModalOpen.value = true
}

const onCloseReviewModal = () => {
  reviewModalOpen.value = false
  activeLedgerId.value = null
}

const viewAttachments = (attachments) => {
  if (! attachments || ! attachments.length) return
  window.open(attachments[0].path, '_blank')
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

const getCompanyRoles = async () => {
  const res = await fetchCompanyDefaultRoles(`?company_id=${companyId.value}`)
  if (! res.status) return
  companyRoles.value = res.data
}

onMounted(() => {
  store.commit('resetPagination')
  getCompanyRoles()
  fetchWorkflow()
  fetchHome().then((res) => {
    homeContent.value = res.data
  })
  fetchOnApproval()
})
</script>

<style scoped>
.filter-label {
  margin-bottom: 4px;
}
</style>
